import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import {
  getAssignmentStudents,
  getAssessmentTypes,
  getActiveTerms,
  enterMark,
  checkExistingMarks,
} from "../api/services";

function EnterMarks() {

  const { t } = useTranslation();

  const { assignmentId } = useParams();

  const navigate = useNavigate();

  const [students, setStudents] = useState([]);

  const [classSubjectId, setClassSubjectId] = useState(null);

  const [assessmentTypes, setAssessmentTypes] = useState([]);

  const [terms, setTerms] = useState([]);

  const [selectedAssessment, setSelectedAssessment] = useState("");

  const [selectedTerm, setSelectedTerm] = useState("");

  const [scores, setScores] = useState({});

  const [loading, setLoading] = useState(true);

  const [saving, setSaving] = useState(false);

  const [marksExist, setMarksExist] = useState(false);

  const [checking, setChecking] = useState(false);

  // =====================================================
  // AUTO COMMENT FUNCTION
  // =====================================================

  const getComment = (score) => {

    const value = Number(score);

    if (value < 5) {

      return "Very Poor";
    }

    else if (value < 10) {

      return "Poor";
    }

    else if (value < 13) {

      return "Average";
    }

    else if (value < 16) {

      return "Good";
    }

    else if (value < 18) {

      return "Very Good";
    }

    else {

      return "Excellent";
    }
  };

  // =====================================================
  // FETCH DATA
  // =====================================================

  const fetchData = useCallback(async () => {

    try {

      const [
        studentsRes,
        assessRes,
        termsRes
      ] = await Promise.all([

        getAssignmentStudents(assignmentId),

        getAssessmentTypes(),

        getActiveTerms(),
      ]);

      setStudents(studentsRes.data.students);

      setClassSubjectId(
        studentsRes.data.class_subject
      );

      setAssessmentTypes(
        assessRes.data
      );

      setTerms(
        termsRes.data
      );

    } catch (err) {

      console.error(
        "Failed to load data",
        err
      );

      alert(
        "Failed to load page data"
      );

    } finally {

      setLoading(false);
    }

  }, [assignmentId]);

  useEffect(() => {

    fetchData();

  }, [fetchData]);

  // =====================================================
  // CHECK IF MARKS EXIST
  // =====================================================

  useEffect(() => {

    const runCheck = async () => {

      if (
        !selectedAssessment ||
        !selectedTerm ||
        !classSubjectId
      ) {

        setMarksExist(false);

        return;
      }

      try {

        setChecking(true);

        const res =
          await checkExistingMarks({

          class_subject:
            classSubjectId,

          assessment_type:
            selectedAssessment,

          term:
            selectedTerm,
        });

        setMarksExist(
          res.data.exists
        );

      } catch (err) {

        console.error(
          "Check failed",
          err
        );

      } finally {

        setChecking(false);
      }
    };

    runCheck();

  }, [
    selectedAssessment,
    selectedTerm,
    classSubjectId
  ]);

  // =====================================================
  // HANDLE SCORE CHANGE
  // =====================================================

  const handleScoreChange = (
    studentId,
    value
  ) => {

    setScores((prev) => ({

      ...prev,

      [studentId]: value,
    }));
  };

  // =====================================================
  // SAVE MARKS
  // =====================================================

  const handleSave = async () => {

    if (
      !selectedAssessment ||
      !selectedTerm
    ) {

      alert(
        "Please select assessment and term first."
      );

      return;
    }

    if (marksExist) {

      alert(
        "Marks already exist for this assessment and term."
      );

      return;
    }

    setSaving(true);

    try {

      const requests =
        Object.entries(scores)

        .filter(
          ([_, value]) =>
            value !== ""
        )

        .map(
          ([studentId, value]) =>

          enterMark({

            student: studentId,

            class_subject:
              classSubjectId,

            assessment_type:
              selectedAssessment,

            term:
              selectedTerm,

            score: value,
          })
        );

      await Promise.all(requests);

      alert(
        "Marks saved successfully!"
      );

      navigate(-1);

    } catch (err) {

      console.error(err);

      alert(
        "Failed to save some marks"
      );

    } finally {

      setSaving(false);
    }
  };

  // =====================================================
  // PROGRESS
  // =====================================================

  const enteredCount =
    Object.keys(scores).length;

  const totalStudents =
    students.length;

  const progress =
    totalStudents === 0
      ? 0
      : Math.round(
          (
            enteredCount /
            totalStudents
          ) * 100
        );

  return (

    <div style={styles.page}>

      {/* HEADER */}

      <div style={styles.header}>

        <h2>
          {t("enterMarks")}
        </h2>

        <button
          style={styles.backBtn}
          onClick={() =>
            navigate(
              "/teacher/my-assignments"
            )
          }
        >
          ← {t("back")}
        </button>

      </div>

      {loading ? (

        <p>
          Loading data...
        </p>

      ) : (

        <>

          {/* WARNING */}

          {marksExist && (

            <div style={styles.warningBox}>

              ⚠️ Marks already exist
              for this assessment
              and term.
              Editing is disabled, Contact Administration.

            </div>
          )}

          {/* SELECTORS */}

          <div style={styles.selectors}>

            <div style={styles.selectGroup}>

              <label style={styles.label}>
                {t("assessments")}
              </label>

              <select
                style={styles.select}
                value={selectedAssessment}
                onChange={(e) =>
                  setSelectedAssessment(
                    e.target.value
                  )
                }
              >

                <option value="">
                  -- {t("selectAssessments")} --
                </option>

                {assessmentTypes.map((a) => (

                  <option
                    key={a.id}
                    value={a.id}
                  >
                    {a.name}
                  </option>

                ))}

              </select>

            </div>

            <div style={styles.selectGroup}>

              <label style={styles.label}>
                {t("term")}
              </label>

              <select
                style={styles.select}
                value={selectedTerm}
                onChange={(e) =>
                  setSelectedTerm(
                    e.target.value
                  )
                }
              >

                <option value="">
                  -- {t("selectTerm")} --
                </option>

                {terms.map((t) => (

                  <option
                    key={t.id}
                    value={t.id}
                  >
                    {t.name}
                    {" - "}
                    {t.session__name}
                  </option>

                ))}

              </select>

            </div>

          </div>

          {/* TABLE */}

          <div style={styles.tableWrapper}>

            <table style={styles.table}>

              <thead>

                <tr>

                  <th style={styles.thLeft}>
                    Student Name
                  </th>

                  <th style={styles.thCenter}>
                    Score / 20
                  </th>

                  <th style={styles.thCenter}>
                    Comment
                  </th>

                </tr>

              </thead>

              <tbody>

                {students.map((s) => (

                  <tr key={s.id}>

                    <td style={styles.nameCell}>

                      {s.first_name}
                      {" "}
                      {s.last_name}

                    </td>

                    <td style={styles.scoreCell}>

                      <input
                        type="number"
                        min="0"
                        max="20"
                        disabled={marksExist}
                        style={styles.input}
                        onChange={(e) =>
                          handleScoreChange(
                            s.id,
                            e.target.value
                          )
                        }
                      />

                    </td>

                    <td style={styles.commentCell}>

                      {scores[s.id]
                        ? getComment(scores[s.id])
                        : "-"}

                    </td>

                  </tr>

                ))}

              </tbody>

            </table>

          </div>

          {/* PROGRESS */}

          <div style={styles.progressBox}>

            <div style={styles.progressLabel}>

              Progress:
              {" "}
              {enteredCount}
              /
              {totalStudents}

            </div>

            <div style={styles.progressBar}>

              <div
                style={{
                  ...styles.progressFill,
                  width: `${progress}%`,
                }}
              />

            </div>

          </div>

          {/* SAVE BUTTON */}

          <button
            style={{
              ...styles.saveBtn,

              opacity:
                !selectedAssessment ||
                !selectedTerm ||
                saving ||
                marksExist
                  ? 0.6
                  : 1,

              cursor:
                !selectedAssessment ||
                !selectedTerm ||
                saving ||
                marksExist
                  ? "not-allowed"
                  : "pointer",
            }}

            disabled={
              !selectedAssessment ||
              !selectedTerm ||
              saving ||
              marksExist
            }

            onClick={handleSave}
          >

            {saving
              ? "Saving..."
              : "Save Marks"}

          </button>

        </>
      )}

    </div>
  );
}

const styles = {

  page:{
    padding:"7px",
    fontFamily:
      "Segoe UI, sans-serif",
    maxWidth:"1100px",
    margin:"0 auto",
  },

  header:{
    display:"flex",
    justifyContent:"space-between",
    alignItems:"center",
    marginBottom:"20px",
  },

  backBtn:{
    padding:"6px 12px",
    background:"#64748b",
    color:"white",
    border:"none",
    borderRadius:"6px",
    cursor:"pointer",
  },

  warningBox:{
    background:"#fef3c7",
    color:"#f30808",
    padding:"12px",
    borderRadius:"8px",
    marginBottom:"16px",
    fontWeight:"600",
  },

  progressBox:{
    marginTop:"20px"
  },

  progressLabel:{
    marginBottom:"6px",
    fontWeight:"600"
  },

  progressBar:{
    height:"10px",
    background:"#e5e7eb",
    borderRadius:"6px",
    overflow:"hidden",
  },

  progressFill:{
    height:"100%",
    background:"#22c55e"
  },

  selectors:{
    display:"flex",
    gap:"20px",
    marginBottom:"20px",
    flexWrap:"wrap",
  },

  selectGroup:{
    display:"flex",
    flexDirection:"column",
    minWidth:"220px",
  },

  label:{
    marginBottom:"6px",
    fontWeight:"600"
  },

  select:{
    padding:"8px",
    borderRadius:"6px",
    border:"1px solid #cbd5e1",
  },

  tableWrapper:{
    border:"1px solid #e2e8f0",
    borderRadius:"10px",
    overflow:"hidden",
  },

  table:{
    width:"100%",
    borderCollapse:"collapse"
  },

  thLeft:{
    textAlign:"left",
    padding:"12px",
    background:"#f1f5f9",
  },

  thCenter:{
    textAlign:"center",
    padding:"12px",
    background:"#f1f5f9",
  },

  nameCell:{
    padding:"10px 12px",
    textAlign:"left",
    borderTop:"1px solid #e2e8f0",
  },

  scoreCell:{
    textAlign:"center",
    borderTop:"1px solid #e2e8f0",
  },

  commentCell:{
    textAlign:"center",
    borderTop:"1px solid #e2e8f0",
    fontWeight:"600",
    color:"#2563eb"
  },

  input:{
    width:"80px",
    padding:"6px",
    textAlign:"center",
  },

  saveBtn:{
    marginTop:"24px",
    padding:"12px 20px",
    background:"#16a34a",
    color:"white",
    border:"none",
    borderRadius:"8px",
    fontWeight:"600",
  },
};

export default EnterMarks;